import * as React from "react";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FC } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessListingPage } from "@app/access-reports/shared/accessListingPage";
import { AccessOverviewPage } from "@app/access-reports/shared/accessOverviewPage";
import { AccessRadiusSearchPage } from "@app/access-reports/shared/accessRadiusSearchPage";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsWizardDetailsPage } from "@app/access-reports/shared/accessReportsWizardDetailsPage";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardEmployeeCountPage } from "@app/access-reports/shared/accessReportsWizardEmployeeCountPage";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardGroupPropertyIdEnum } from "@app/access-reports/shared/accessReportsWizardGroupPropertyIdEnum";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardProviderCountPage } from "@app/access-reports/shared/accessReportsWizardProviderCountPage";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardSummaryPage } from "@app/access-reports/shared/accessReportsWizardSummaryPage";

interface AccessReportsWizardFormProps {
  accessReportParameters: AccessReportParametersModel;
  formGroups: AccessReportsWizardFormGroup[];
  getDisabledStatus: (formGroup: AccessReportsWizardFormGroup, element: AccessReportsWizardElement, byProductDefaultId?: number) => boolean;
  invalidElements: Map<string, string>;
  invalidPageOptionPageElements: Map<string, string>;
  onAddPageClick: (groupProperty: AccessReportsWizardGroupPropertyIdEnum) => void;
  onCheckboxChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, byProductDefaultId?: number) => void;
  onInputChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, byProductDefaultId?: number, event?: React.ChangeEvent<HTMLInputElement>, currentValue?: string,) => void;
  onRemovePageClick: (groupProperty: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number) => void;
  onSelectChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, event: React.ChangeEvent<HTMLSelectElement>, byProductDefaultId?: number) => void;
  productId?: number;
  repeatedByProductDefaultIds: number[];
  unrequiredValidationElements: string[];
  wizardStep: AccessReportsWizardStepEnum;
}

const AccessReportsWizardForm: FC<AccessReportsWizardFormProps> = (props: AccessReportsWizardFormProps) => {
  const buildFormGroup = (formGroup: AccessReportsWizardFormGroup): React.ReactNode => {
    let isVisible = false;
    if (!formGroup.hidden) {
      // if a form group is flat-out marked as hidden, do not display it
      // this takes precedence over anything else
      if (!formGroup.groupPropertyId) {
        isVisible = true;
      } else {
        if (!!props.accessReportParameters && !!props.accessReportParameters.product) {
          if (!formGroup.hiddenProducts?.includes(props.accessReportParameters.product.id)) {
            isVisible = true;
          }
        }
      }
    }

    if (isVisible) {
      if (!!formGroup.groupPropertyId) {
        const pages = props.accessReportParameters[formGroup.groupPropertyId];

        return (
          <React.Fragment>
            {pages.map((page, index) => {
              let isRepeated = false;

              if (props.repeatedByProductDefaultIds?.includes(page.id)) {
                isRepeated = true;
              }

              return (
                <div className="row align-items-center" key={index}>
                  {(!!formGroup.elements && !isRepeated) &&
                    buildFormElement(formGroup.elements[0], formGroup, 0, isRepeated, page)
                  }
                  <div className={`col-9 ${isRepeated ? "offset-md-2" : ""}`}>
                    <div className="row">
                      {formGroup.elements?.filter(x => !x.hidden).map((element, index) => {
                        if (index !== 0) {
                          return buildFormElement(element, formGroup, index, isRepeated, page)
                        }
                      })}
                    </div>
                  </div>
                  {isRepeated &&
                    <div className="col-1">
                      <QESButton
                        qesTestId={`access-reports-wizard__form--remove-form-group-${formGroup.id}`}
                        qesType="secondary-danger"
                        className="me-3"
                        onClick={() => props.onRemovePageClick(formGroup.groupPropertyId, page.id)}
                      >
                        Remove
                      </QESButton>
                    </div>
                  }
                </div>
              );
            })}

            {(!formGroup.disallowedMultipleProducts?.includes(props.productId) && !formGroup.hiddenProducts?.includes(props.productId)) && (
              <div className="row">
                <div className="offset-md-2 col-md-10">
                  <QESButton
                    className="p-0"
                    qesTestId={`access-reports-wizard__form--add-another-form-group-${formGroup.id}`}
                    qesType="secondary"
                    onClick={() => props.onAddPageClick(formGroup.groupPropertyId)}
                  >
                    <QesIcon iconName={QesIcons.Plus} description="Add" className="mb2" /> Add another
                  </QESButton>
                </div>
              </div>
            )}
            <hr />
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment>
            {formGroup.title &&
              <div className="row">
                <div className="col-12">
                  <label className="label-m mb-2">
                    {formGroup.title}
                  </label>
                </div>
              </div>
            }

            <div className="row">
              {formGroup.elements?.filter(x => !x.hidden).map((element, index) => {
                return buildFormElement(element, formGroup, index, false)
              })}
            </div>
          </React.Fragment>
        );
      }
    }
  }

  const buildFormElement = (element: AccessReportsWizardElement, formGroup: AccessReportsWizardFormGroup, index: number, isRepeatedFormGroup: boolean, page?: AccessListingPage | AccessOverviewPage | AccessRadiusSearchPage | AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessReportsWizardProviderCountPage): React.ReactNode => {
    let isHiddenByProduct = false;

    if (!!props.accessReportParameters && !!props.accessReportParameters.product) {
      const productOption: AccessReportsWizardOption = !!element.options && element.options.find(x => x.id === props.accessReportParameters.product.id);

      if (!!productOption && !!productOption.hiddenProducts && productOption.hiddenProducts.includes(props.accessReportParameters.product.id)) {
        isHiddenByProduct = true;
      }
    }

    if (!isHiddenByProduct) {
      const disabled: boolean = props.getDisabledStatus(formGroup, element, page?.id);
      const uniquePageIdSuffix = !!page ? `-${page.id}` : "";
      const value = getElementValue(element, formGroup, page);
      let elementNode: React.ReactNode = null;

      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.Checkbox:
          elementNode = (
            <div className="d-flex align-items-center w-100 h-100">
              <QESCheckbox
                checked={value}
                disabled={disabled}
                label={element.title}
                testId={`access-reports-wizard__form--${element.elementId}-checkbox${uniquePageIdSuffix}`}
                onChange={() => props.onCheckboxChange(props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id)}
              />
            </div>
          );

          break;
        case AccessReportsWizardElementTypeEnum.Dropdown:
          let options: AccessReportsWizardOption[] = [];
          if (!!element.previousRequiredElementId && element.usePreviousElementFilterId) {
            const previousRequiredElement: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

            if (!!previousRequiredElement) {
              const previousRequiredElementValue = getElementValue(previousRequiredElement, formGroup, page);

              if (previousRequiredElementValue !== null && previousRequiredElementValue !== undefined) {
                options = element.options.filter((x) => ((x.previousElementFilterId !== null && x.previousElementFilterId !== undefined) && (x.previousElementFilterId.toString() === previousRequiredElementValue.toString())));
              }
            }
          } else {
            options = element.options;
          }

          if (!!props.accessReportParameters && !!props.accessReportParameters.product) {
            options = options.filter((x) => (!!x.hiddenProducts && !x.hiddenProducts.includes(props.accessReportParameters.product.id)));
          }

          elementNode = (
            <QESSelect
              allowEmpty={true}
              containerClassName="mb-3"
              disabled={disabled}
              isValid={!element.required || element.required && !!value}
              testId={`access-reports-wizard__form--${element.elementId}-dropdown${uniquePageIdSuffix}`}
              label={element.title}
              required={element.required && !disabled}
              onChange={(e) => props.onSelectChange(props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, e, page?.id)}
              value={value}
            >
              {options && options.map((option, optionIndex) => (
                <option
                  key={optionIndex}
                  value={option.id}
                >
                  {option.displayName ? option.displayName : option.name}
                </option>
              ))}
            </QESSelect>
          );

          break;
        case AccessReportsWizardElementTypeEnum.Input:
          elementNode = (
            <QESTextInput
              className="mb-3"
              testId={`access-reports-wizard__form--${element.elementId}-input${uniquePageIdSuffix}`}
              disabled={disabled}
              errorText={!!formGroup.groupPropertyId ? props.invalidPageOptionPageElements.get(page?.id + element.elementId) : props.invalidElements.get(element.elementId)}
              isValid={!!formGroup.groupPropertyId ? !props.invalidPageOptionPageElements.get(page?.id + element.elementId) : !props.invalidElements.get(element.elementId)}
              label={element.title || " ".replace(/ /g, "\u00a0")}
              onChange={(e) => props.onInputChange(props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id, e)}
              onBlur={() => props.onInputChange(props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id, null, value?.toString())}
              required={element.required}
              unrequiredValidation={props.unrequiredValidationElements.includes(element.elementId)}
              value={value}
            />
          );

          break;
        default:
          break;
      }

      return (
        <div
          className={`col-${(!!formGroup.groupPropertyId && !isRepeatedFormGroup && index === 0) ? "2" : (!!formGroup.groupPropertyId) ? "4" : "3"} px-2`}
          key={index}
        >
          {elementNode}
        </div>
      );
    }
  }

  /* oxlint-disable @typescript-eslint/no-explicit-any */
  const getElementValue = (element: AccessReportsWizardElement, formGroup: AccessReportsWizardFormGroup, page?: AccessListingPage | AccessOverviewPage | AccessRadiusSearchPage | AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessReportsWizardProviderCountPage) => {
    if (formGroup.groupPropertyId) {
      const formGroupModels = (props.accessReportParameters as any)[formGroup.groupPropertyId];
      const formGroupModel = formGroupModels?.find((x: any) => x.id === page.id)

      if (!!formGroupModel) {
        if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
          if (formGroupModel[element.elementId]) {
            return formGroupModel[element.elementId].id.toString();
          }
        } else {
          return formGroupModel[element.elementId];
        }
      }
    } else {
      if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
        if ((props.accessReportParameters as any)[element.elementId]) {
          return (props.accessReportParameters as any)[element.elementId].id.toString();
        } else {
          return "";
        }

      } else {
        if (element.elementId === AccessReportsCoverPageElementEnum.To) {
          return props.accessReportParameters.accessSharedOutput?.to;
        } else {
          return (props.accessReportParameters as any)[element.elementId];
        }
      }
    }

    return "";
  }
  /* oxlint-enable @typescript-eslint/no-explicit-any */

  const shouldBuildFormGroup = (formGroup: AccessReportsWizardFormGroup): boolean => {
    const hiddenElements: AccessReportsWizardElement[] = formGroup.elements.filter(x => x.hidden);

    if (formGroup.hidden || hiddenElements.length === formGroup.elements.length) {
      // if all the elements are hidden (or the form group itself is marked as hidden), do not display the form group
      return false;
    }

    return true;
  }

  return (
    <React.Fragment>
      {props.formGroups ?
        props.formGroups.map((formGroup, index) => (
          <React.Fragment key={index}>
            {shouldBuildFormGroup(formGroup) && buildFormGroup(formGroup)}
          </React.Fragment>
        )) :
        <div className="row">
          <div className="col-12">
            <div className="text-center lead mb-3">
              <LoadingSpinner /> Loading...
            </div>
          </div>
        </div>
      }
    </React.Fragment>
  );
}

export { AccessReportsWizardForm };
